import _ from 'lodash';

/**
 * Cost Summary review — which line items are allowed to have no supplier.
 *
 * The red "no supplier" indicator fires on any line item that isn't linked to a
 * service provider. A handful of lines never have one by design (statutory
 * levies, fees we charge ourselves, medical certificates billed by the
 * practitioner), so they'd sit permanently red and train reviewers to ignore the
 * colour. Those paths are exempt.
 *
 * The list is seeded here and overridable from the Accounts page
 * (`account.data.costSummary.supplierExemptPaths`) so it can be adjusted without
 * a release.
 *
 * Exempt from RED is not exempt from ORANGE — an exempt line still flags when
 * its price was manually changed, which is the point for fees and certificates.
 */

/**
 * Matching is on WHOLE dotted paths, never substrings. A substring rule like
 * `cremation` would silently exempt every `disposal.cremation.lineItems.*`
 * product line, which is exactly the leakage the indicator exists to catch.
 */
export const DEFAULT_SUPPLIER_EXEMPT_PATHS: string[] = [
  // Medical certificates — billed by the certifying practitioner, no provider
  // record. `attendingPractitionerMedicalCertificateCauseOfDeath` is built by
  // two branches (burial and cremation) under the same path.
  'medicalCertificate.attendingPractitionerMedicalCertificateCauseOfDeath',
  'medicalCertificate.attendingPractitionerCremationRiskAdvice',
  'medicalCertificate.medicalRefereeCremationPermit',
  // Picaluna's own fees.
  'preparation.prepaidEstablishmentFee',
  'administrationFee',
  // NSW statutory levies — payable to the state, not to a supplier.
  'nswCemeteryAndCremCost.cremationFee',
  'nswCemeteryAndCremCost.ashIntermentFee',
  'nswCemeteryAndCremCost.burialFee',
  // Death certificates lodged with the registry.
  'birthsDeathsAndMarriages.nsw.lineItems',
  'birthsDeathsAndMarriages.vic.lineItems',
  'birthsDeathsAndMarriages.qld.lineItems',
];

/**
 * Drops the array-index segments from an invoice item's form path so a rule can
 * name a collection once instead of every row in it:
 * `birthsDeathsAndMarriages.nsw.lineItems.2` → `birthsDeathsAndMarriages.nsw.lineItems`.
 */
export function normaliseInvoiceItemPath(path?: string | null): string {
  if (!path) {
    return '';
  }

  return path
    .split('.')
    .filter((segment) => !/^\d+$/.test(segment))
    .join('.');
}

/**
 * The configured list, falling back to the seeded defaults when the account
 * carries nothing. Tolerates the FormGenerator array shape
 * (`{ 'id-x': { _meta: { value } } }`) as well as a plain array — the actual
 * precedent (`financeExport.recipientUserIds`) persists a plain array.
 */
export function readSupplierExemptPaths(account: any): string[] {
  const raw = _.get(account, 'data.costSummary.supplierExemptPaths');

  if (raw === undefined || raw === null) {
    return [...DEFAULT_SUPPLIER_EXEMPT_PATHS];
  }

  const entries = Array.isArray(raw) ? raw : Object.values(raw || {});

  const paths = entries
    .map((entry: any) =>
      entry && typeof entry === 'object' && entry._meta ? entry._meta.value : entry
    )
    .filter((value: any): value is string => typeof value === 'string' && value.trim() !== '')
    .map((value: string) => value.trim());

  // An account that has been saved with an empty list means "exempt nothing" —
  // only an absent key falls back to the defaults.
  return paths;
}

/**
 * Whether a line item at `path` is allowed to have no supplier. Compares the
 * full path and its index-stripped form against each rule, both ways, so a rule
 * may name either a specific leaf or the collection that holds it.
 */
export function isSupplierExemptPath(path: string | undefined | null, exemptPaths: string[]): boolean {
  if (!path) {
    return false;
  }

  const normalisedPath = normaliseInvoiceItemPath(path);

  return exemptPaths.some((rule) => {
    const normalisedRule = normaliseInvoiceItemPath(rule);

    return (
      rule === path ||
      normalisedRule === normalisedPath ||
      normalisedRule === path ||
      rule === normalisedPath
    );
  });
}
